import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';

import { Table } from 'src/components/TableNew';
import NoResult from 'src/components/NoResult/NoResult';
import { CopyableCell } from 'src/components/CopyableCell';

import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import {
  fetchInternalWallets,
  formatNetworkLabel,
  formatWalletBalance,
  type InternalWallet,
} from 'src/api/adminWallets';
import { convertDate, shortenString } from 'src/components/Table/helpers';

const HEADERS = ['Currency', 'Balance', 'Address', 'Platform', 'Platform ID', 'Created at'];

type InternalWalletsTabProps = {
  onTotalChange?: Dispatch<SetStateAction<number | null>>;
};

/**
 * The platform's own treasury wallets. Read-only: the endpoint takes page and
 * limit only, and there is no admin write side for these.
 */
const InternalWalletsTab: React.FC<InternalWalletsTabProps> = ({ onTotalChange }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [wallets, setWallets] = useState<InternalWallet[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchInternalWallets({ page: currentPage, limit: 10 });
      setWallets(res.wallets);
      setTotalPages(Math.max(1, res.meta?.page_count || 1));
      onTotalChange?.(res.meta?.total_count || 0);
    } catch (e: any) {
      dispatch(
        setAppAlert({
          message: e?.response?.data?.error ?? 'Failed to load internal wallets',
          isSuccess: false,
        }),
      );
      setWallets([]);
      onTotalChange?.(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, dispatch, onTotalChange]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(
    () =>
      wallets.map((wallet) => {
        const created = wallet.created_at ? convertDate(wallet.created_at) : null;
        return {
          ...wallet,
          ccy: wallet.currency_code || '—',
          balance_display: formatWalletBalance(wallet.balance, wallet.currency_code),
          address_display: wallet.address || '—',
          platform_display: wallet.platform ? formatNetworkLabel(wallet.platform) : '—',
          platform_id_display: wallet.platform_id ? shortenString(wallet.platform_id) : '—',
          created_display: created ? `${created.date} ${created.time}` : '—',
        };
      }),
    [wallets],
  );

  return rows.length > 0 || loading ? (
    <Table
      loading={loading}
      headers={HEADERS}
      items={rows}
      columns="0.7fr 1fr 1.4fr 0.9fr 0.9fr 1.1fr"
      fieldsToShow={[
        'ccy',
        'balance_display',
        'address_display',
        'platform_display',
        'platform_id_display',
        'created_display',
      ]}
      totalPages={totalPages}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      disableRowDetails
      renderCell={(item, field) => {
        if (field === 'address_display') {
          return item.address_display === '—' ? (
            <span>—</span>
          ) : (
            <CopyableCell value={item.address_display} label="Address" />
          );
        }
        if (field === 'platform_id_display') {
          return item.platform_id ? (
            <CopyableCell value={item.platform_id} label="Platform ID" />
          ) : (
            <span>—</span>
          );
        }
        return null;
      }}
    />
  ) : (
    <NoResult />
  );
};

export default InternalWalletsTab;
